"use client";

import { useEffect } from "react";
import { initPerf, perf } from "@/lib/perf";

// 전역 텔레메트리 부트스트랩 — root layout 에 1회 마운트. UI 없음(null).
// requestIdleCallback 안에서만 초기화해 LCP/하이드레이션에 영향 0.
// Web Vitals 는 동적 import 로 초기 번들에서 제외.
export function PerfCollector() {
  useEffect(() => {
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      if (!initPerf()) return; // 비표본 세션이면 아무것도 안 함
      collectNavTiming();
      collectWebVitals();
      collectLongTasks();
    };

    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(start, { timeout: 4000 });
    } else {
      timeoutId = setTimeout(start, 1200); // iOS Safari 등 rIC 미지원 폴백
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(idleId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  return null;
}

function collectNavTiming() {
  const emit = () => {
    try {
      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      if (!nav) return;
      const round = (n: number) => (Number.isFinite(n) && n >= 0 ? Math.round(n) : undefined);
      perf("page_load", nav.loadEventEnd - nav.startTime, {
        ttfb: round(nav.responseStart - nav.startTime),
        domInteractive: round(nav.domInteractive - nav.startTime),
        dcl: round(nav.domContentLoadedEventEnd - nav.startTime),
        transferKB: nav.transferSize ? Math.round(nav.transferSize / 1024) : undefined,
        navType: nav.type,
        dpr: window.devicePixelRatio,
        screen: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : undefined,
        cores: (navigator as unknown as { hardwareConcurrency?: number }).hardwareConcurrency,
        mem: (navigator as unknown as { deviceMemory?: number }).deviceMemory,
      });
    } catch {
      /* noop */
    }
  };
  if (document.readyState === "complete") emit();
  else window.addEventListener("load", () => setTimeout(emit, 0), { once: true });
}

function collectWebVitals() {
  // web-vitals 미설치/로드 실패해도 page_load 는 동작 — 조용히 무시.
  import("web-vitals")
    .then((wv) => {
      const rate = (name: string, scale = 1) => (m: { value: number; rating?: string; id?: string }) =>
        perf(name, m.value * scale, { value: m.value, rating: m.rating, id: m.id });
      wv.onLCP?.(rate("wv_lcp"));
      wv.onINP?.(rate("wv_inp"));
      wv.onTTFB?.(rate("wv_ttfb"));
      wv.onCLS?.(rate("wv_cls", 1000)); // CLS 는 무차원 → *1000 정수 근사(원값은 meta.value)
    })
    .catch(() => {});
  // 운영 콘솔(/manage)에선 느린 조작(0.2초+)이 **무엇**이었고 어느 단계가 느렸는지까지 — 입력 대기(다른 일에 막힘)·
  // 처리(우리 JS)·그리기(스타일/레이아웃/페인트). 09-22 "렉 걸린다" 신고 때 INP 448ms 만 보이고 어느 칸인지 몰랐다.
  if (location.pathname.startsWith("/manage")) {
    import("web-vitals/attribution")
      .then((wa) => {
        wa.onINP((m) => {
          if (m.value < 200) return;
          const a = m.attribution;
          perf("inp_attr", m.value, {
            target: a.interactionTarget?.slice(0, 160),
            type: a.interactionType,
            input: Math.round(a.inputDelay),
            processing: Math.round(a.processingDuration),
            presentation: Math.round(a.presentationDelay),
          });
        }, { reportAllChanges: true });
      })
      .catch(() => {});
  }
}

// 화면이 오래 멈춘 순간 — 1초 넘게 멈춘 긴 작업만 남긴다(크롬 계열만 longtask 를 준다).
// 계기(2026-09-22): "지도영상 고르면 오랫동안 멈추고 응답 없음" 신고 — 서버 로그·API 시간엔 흔적이 없었고 재현도 안 됐다.
// 운영 콘솔(/manage)에서만 직전에 누른 것(title·aria-label·글자 40자)을 같이 적는다 — 다른 화면엔 수련생 이름 같은 게
// 버튼 글자로 있을 수 있어 길이만 남긴다. 멈춘 동안엔 못 보내므로 풀린 뒤 큐에 넣고 다음 flush 로 나간다.
function collectLongTasks() {
  try {
    if (typeof PerformanceObserver === "undefined" || !PerformanceObserver.supportedEntryTypes?.includes("longtask")) return;
    const labelled = location.pathname.startsWith("/manage");
    let last = "";
    let lastAt = 0;
    if (labelled) {
      addEventListener("pointerdown", (e) => {
        const el = (e.target as Element | null)?.closest?.("[title],[aria-label],button,[role=option],[role=combobox],a");
        last = (el?.getAttribute("title") || el?.getAttribute("aria-label") || el?.textContent || "").trim().slice(0, 40);
        lastAt = performance.now();
      }, { capture: true, passive: true });
    }
    let sent = 0;
    new PerformanceObserver((list) => {
      for (const en of list.getEntries()) {
        if (en.duration < 1000 || sent >= 20) continue;
        sent++;
        perf("long_task", en.duration, labelled
          ? { lastAction: last || undefined, sinceActionMs: lastAt ? Math.round(en.startTime - lastAt) : undefined }
          : undefined);
      }
    }).observe({ type: "longtask", buffered: true });
  } catch {
    /* noop */
  }
}
